import { el, setText } from '@/lib/dom'
import { FocusGrid, type FocusItem } from '@/lib/focus'
import { T } from '@/lib/i18n'
import type { RemoteKey } from '@/lib/keys'
import { IS_BROWSER } from '@/lib/platform'
import { isValidPinLength, PIN_LONG_LENGTH } from '@/lib/profiles'

/** Təsdiq düyməsinin onay ikonu — `browser/icons.ts`-dəki `ICON_CHECK` ilə eyni cizgi (bu fayl ortaqdır, oranı import etmir). */
const ICON_OK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 13 4 4 10-10"/></svg>'

/**
 * PIN PAD-I — PIN-li profilə keçidin qapısı. İKİ HƏDƏF ÜÇÜN BİR NÜSXƏ.
 *
 * ⚠⚠ NİYƏ `screens/profiles.ts`-DƏN BURAYA KÖÇÜRÜLDÜ (2026-08-30).
 *    NƏ ÖLÇÜLDÜ (canlı tv.kinobox.az, TestingAI veb TV 60 · 66): «Kim baxır?»
 *    ekranında hasPin=true olan «AITest» profilinin kartına klik edildi —
 *    `querySelector('.pinpad, .web-pinpad, [class*=pin]')` NULL qaytardı, sinif
 *    adında «pin» olan 0 element vardı, heç bir `/pin` ünvanına sorğu getmədi,
 *    `kb_tv_profile` dərhal həmin profilin id-si ilə yazıldı və ana ekran açıldı
 *    (171 kart). Uşaq profilindən («Ləman», data-kids=12) PIN-li BÖYÜK profilə
 *    keçid də eyni cür açıldı — yəni uşaq valideynin profilinə maneəsiz keçirdi.
 *    ƏVVƏL NƏ OLURDU: pad YALNIZ cihaz build-ində (`screens/profiles.ts`) vardı;
 *    brauzer variantının `browser/screens/account.ts` → `tile()` funksiyası
 *    `profile.hasPin`-ə ÜMUMİYYƏTLƏ baxmırdı.
 *    NİYƏ MƏHZ BU HƏLL: pad-ın ikinci nüsxəsini yazmaq PIN qaydasını (4 və ya 6
 *    rəqəm, avtomatik göndərmə yoxdur, rəqəm ekranda görünmür, yoxlama serverdə)
 *    iki yerdə saxlamaq demək olardı — biri düzələndə o biri arxada qalardı
 *    (`lib/compat.ts`-dəki fetch/XHR dərsi). İndi hər iki hədəf EYNİ pad-ı, eyni
 *    `verifyProfilePin` çağırışını və eyni DOM-u işlədir.
 *
 * ⚠⚠ AVTOMATİK GÖNDƏRMƏ YOXDUR: PIN 4 VƏ YA 6 rəqəmdir (sahib tələbi 2026-08-26),
 *    yəni 4-cü rəqəmdə avtomatik yoxlama 6 rəqəmli PIN-i həmişə «yanlış» sayardı.
 *    «Təsdiqlə» yalnız qanuni uzunluqda işləyir.
 * ⚠ RƏQƏMLƏR EKRANDA GÖSTƏRİLMİR — nöqtə çəkilir (TV-yə uzaqdan baxan PIN-i görməsin).
 * ⚠ ALTI XANA HƏMİŞƏ ÇƏKİLİR: xana sayı PIN-in uzunluğunu ifşa etməməlidir.
 * ⚠ ES2015 həddi (PERFORMANCE.md): `??`/`?.` yoxdur, DOM ölçülmür, fokus `FocusGrid`-lədir.
 */

/** PIN pad-ının ictimai səthi — ekran onu qat kimi idarə edir. */
export interface PinPad {
  node: HTMLElement
  takeFocus: () => void
  onKey: (key: RemoteKey) => void
  destroy: () => void
}

export function createPinPad(
  profileName: string,
  onVerify: (pin: string) => Promise<boolean>,
  onCancel: () => void,
): PinPad {
  const node = el('div', 'pinpad')
  const box = el('div', 'pinpad__box')
  node.appendChild(box)

  box.appendChild(el('p', 'pinpad__title', T('«{name}» üçün PIN').replace('{name}', profileName)))
  const dots = el('p', 'pinpad__dots')
  box.appendChild(dots)
  const hint = el('p', 'pinpad__hint', T('4 və ya 6 rəqəm'))
  box.appendChild(hint)

  const grid = el('div', 'pinpad__grid')
  box.appendChild(grid)

  let pin = ''
  let busy = false
  let closed = false
  const focus = new FocusGrid()

  function paint(): void {
    let text = ''
    for (let i = 0; i < PIN_LONG_LENGTH; i++) text += i < pin.length ? '●' : '○'
    setText(dots, text)
  }
  paint()

  function press(digit: string): void {
    if (busy || pin.length >= PIN_LONG_LENGTH) return
    setText(hint, T('4 və ya 6 rəqəm'))
    pin += digit
    paint()
  }

  function erase(): void {
    if (busy || pin.length === 0) return
    pin = pin.slice(0, pin.length - 1)
    paint()
  }

  function submit(): void {
    if (busy || !isValidPinLength(pin.length)) return
    busy = true
    setText(hint, T('Yoxlanılır…'))
    onVerify(pin).then((ok) => {
      if (closed) return
      busy = false
      if (ok) return
      pin = ''
      paint()
      setText(hint, T('PIN yanlışdır. Yenidən yoxlayın.'))
    }, () => {
      if (closed) return
      busy = false
      setText(hint, T('Profillər alınmadı. Geri qayıdıb yenidən cəhd edin.'))
    })
  }

  // Sıralar: 1-2-3 · 4-5-6 · 7-8-9 · ⌫-0-✓ (təsdiq ikonu; etiket yalnız oxuyucu üçündür)
  const rows = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['⌫', '0', T('Təsdiqlə')],
  ]
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r]
    if (!row) continue
    const items: FocusItem[] = []
    const line = el('div', 'pinpad__row')
    for (let c = 0; c < row.length; c++) {
      const label = row[c]
      if (label === undefined) continue
      const isOkKey = r === 3 && c === 2
      /* 2026-10-06: veb TV-də pad açıqkən oxlar arxadakı kartlara gedirdi;
         rəqəmlər tabIndex=-1 olan div idi. Brauzerdə doğma button həm ox/Tab
         fokusuna, həm Enter klikə qoşulur; cihazın arifmetik şəbəkəsi dəyişmir. */
      const key = el(IS_BROWSER ? 'button' : 'div', 'pinpad__key focusable', isOkKey ? '' : label)
      if (isOkKey) {
        /*
          ⚠ TƏSDİQ DÜYMƏSİ İKONDUR, YAZI DEYİL (2026-09-25, istifadəçi testi: «Təsdiqlə
            düyməsi də ikonla əvəzlənməlidir» — ⌫ ikondur, yanındakı təsdiq isə uzun
            söz idi və dilə görə düyməyə sığmırdı). Mətn `aria-label`/`title`-dədir.
          ⚠ `innerHTML` YALNIZ SABİT SVG sətri ilə — istifadəçi datası bu yola düşmür.
        */
        key.className = 'pinpad__key pinpad__key--wide pinpad__key--ok focusable'
        key.innerHTML = ICON_OK
        key.setAttribute('aria-label', label)
        key.title = label
      }
      line.appendChild(key)
      const run = (function (value: string, isBack: boolean, isOk: boolean) {
        return function () {
          if (isBack) {
            erase()
            return
          }
          if (isOk) {
            submit()
            return
          }
          press(value)
        }
      })(label, r === 3 && c === 0, r === 3 && c === 2)
      // Brauzerdə doğma button klik/Enter alır, oxları qabığın spatial
      // naviqasiyası idarə edir. Cihazda eyni əməl FocusGrid-ə bağlıdır.
      if (IS_BROWSER) {
        ;(key as HTMLButtonElement).type = 'button'
        key.className += ' web-focusable'
        key.onclick = (event: MouseEvent) => {
          event.stopPropagation()
          if (closed) return
          run()
        }
      }
      items.push({ node: key, onEnter: run })
    }
    grid.appendChild(line)
    focus.addRow({ items })
  }

  /*
    ⚠ BRAUZERDƏ ÇIXIŞ YOLU GÖRÜNƏNDİR. Cihazda pad pultun «Geri»si ilə bağlanır
      (`screens/profiles.ts` → `onKey`), veb TV-də isə həmin düymə pad-a çatmır —
      görünən düymə olmasaydı istifadəçi qatda ilişib qalardı (Frontend/CLAUDE.md §6
      «dead-end yoxdur»). Örtüyün özünə klik də bağlayır — film modalındakı eyni
      qayda (`browser/detailModal.ts` → `root.onclick`).
  */
  if (IS_BROWSER) {
    const cancel = document.createElement('button')
    cancel.type = 'button'
    cancel.className = 'pinpad__cancel web-focusable'
    cancel.appendChild(document.createTextNode(T('Ləğv et')))
    cancel.onclick = (event: MouseEvent) => {
      event.stopPropagation()
      if (closed) return
      onCancel()
    }
    box.appendChild(cancel)
    node.onclick = (event: MouseEvent) => {
      if (event.target === node && !closed) onCancel()
    }
  }

  return {
    node: node,
    takeFocus: function () {
      if (IS_BROWSER) {
        const first = node.querySelector('.pinpad__key') as HTMLElement | null
        if (first) first.focus()
      } else focus.focusFirst()
    },
    onKey: function (key: RemoteKey) {
      if (key === 'back') {
        onCancel()
        return
      }
      if (key === 'enter') {
        focus.enter()
        return
      }
      if (key === 'left' || key === 'right' || key === 'up' || key === 'down') focus.move(key)
    },
    destroy: function () {
      closed = true
      focus.reset()
    },
  }
}
